<template>
  <div>
    <SiteNavigationBar />
    <main
      class="px-4 mb-4 mt-[60px] md:mt-[88px] pt-4 max-lg:max-w-[768px] lg:max-w-[1280px] mx-auto"
    >
      <h1 class="text-center text-xl md:text-2xl lg:text-[2.5rem] mb-4">
        {{ $t('institutional.title') }}
      </h1>
      <div class="title-divider mx-auto mb-4"></div>
      <div class="text-center mb-6">
        {{ $t('institutional.description') }}
      </div>
      <SitePagesResults
        v-model:page="page"
        :items="requestState === 'loading' ? null : items"
        :total-pages="
          requestState === 'loading'
            ? 0
            : Math.ceil(totalItems / ITEMS_PER_PAGE)
        "
      />
    </main>
    <SiteFooter v-once :fix-locale="locale" />
  </div>
</template>

<script setup lang="ts">
import { GET_WP_PAGES } from '@/utils/queries';
import { fixImageUrl } from '@/helpers/fixes';
import type { PageFromResponse, PostCard } from '@/types/common';

const SitePagesResults = defineAsyncComponent(
  () => import('@/async-components/Site/Blog/Results.vue')
);
const SiteNavigationBar = defineAsyncComponent(
  () => import('@/async-components/Site/NavigationBar.vue')
);

type PageCard = Omit<PostCard, 'category' | 'categoryLink'>;

const {
  public: { cloudflareImageProcessing: cfUrl }
} = useRuntimeConfig();

const client = useWpClient();

const page = defineModel<number>('page', {
  default: 1
});

const localePath = useLocalePath();

const { t, locale } = useI18n();

const { language } = useLanguage();

const ITEMS_PER_PAGE = 12;

async function fetchItems(): Promise<{
  state?: 'loading' | 'error' | 'success';
  found: number;
  items: PageCard[];
}> {
  try {
    const gqlResponse = await client.query<{
      pages: {
        nodes: PageFromResponse[];
        pageInfo: {
          offsetPagination: {
            total: number;
          };
        };
      };
    }>({
      query: GET_WP_PAGES,
      variables: {
        perPage: ITEMS_PER_PAGE,
        offset: (page.value - 1) * ITEMS_PER_PAGE,
        language: locale.value === 'us' ? 'en' : locale.value
      }
    });

    const items = gqlResponse.data.pages.nodes.map(node => {
      const to = localePath(`/${t('routes.institutional')}` + `/${node.slug}`);

      const originalImage = node.featuredImage?.node?.sourceUrl;
      const fixedImage = originalImage ? fixImageUrl(originalImage) : '';
      const image = fixedImage
        ? `${cfUrl}/image?w=352&h=202&f=webp&fit=crop&image=${fixedImage}`
        : '';
      const imageSet = fixedImage
        ? `${image} 1x, ${cfUrl}/image?w=704&h=404&f=webp&fit=crop&image=${fixedImage} 2x`
        : '';

      return {
        title: node.title,
        excerpt: node.excerpt,
        to,
        image,
        imageSet,
        imageAlt: node.featuredImage?.node?.altText
      };
    });

    const found =
      gqlResponse.data.pages.pageInfo?.offsetPagination?.total || items.length;

    return {
      state: 'success',
      items,
      found
    };
  } catch (error) {
    console.error('error', error);
    return {
      state: 'error',
      items: [],
      found: 0
    };
  }
}

const { data: asyncResponse, error: fetchError } = await useAsyncData<{
  state?: 'loading' | 'error' | 'success';
  found: number;
  items: PageCard[];
}>('pages', async () => {
  if (process.client) {
    return { found: 0, items: [], state: 'loading' };
  }
  return await fetchItems();
});

if (fetchError?.value) {
  console.error(fetchError.value);
}

const clientResponse = ref<{
  state?: 'loading' | 'error' | 'success';
  found: number;
  items: PageCard[];
}>(
  (!fetchError?.value ? asyncResponse.value : false) || {
    found: 0,
    items: [],
    state: 'loading'
  }
);

onMounted(() => {
  if (!asyncResponse.value?.items?.length) {
    fetchItems().then(response => (clientResponse.value = response));
  }
});

watch(
  () => page.value,
  async () => {
    clientResponse.value = {
      state: 'loading',
      found: 0,
      items: []
    };
    const response = await fetchItems();
    clientResponse.value = response;
  }
);

const items = computed(() => {
  return clientResponse.value?.items || [];
});

const totalItems = computed(() => {
  return clientResponse.value.found;
});

const requestState = computed<'loading' | 'error' | 'success'>(() => {
  return clientResponse.value?.state;
});

useSeoExtended({
  title: t('institutional.title'),
  description: t('institutional.description'),
  image:
    clientResponse.value?.items[0]?.image ||
    asyncResponse.value?.items[0]?.image ||
    '/images/pfs-placeholder.svg'
});

const { link } = useCanonicalLink((t, locale) => {
  const path =
    `/${t('routes.institutional')}` +
    (page.value > 1 ? `/${page.value}-${t('paramsKeys.page')}` : '');
  return localePath(path, locale);
});

const host = useHost();

const breadcrumbs = computed(() => [
  {
    name: t('seo.home'),
    item: host + localePath('/')
  },
  {
    name: t('menu.institutional'),
    item: host + localePath(`/${t('routes.institutional')}`),
    interactuable: false
  }
]);

useSchemaOrg([
  ...useCommonSchemaOrg(link),
  defineBreadcrumb({
    '@id': link + '#breadcrumb',
    itemListElement: breadcrumbs.value.map(({ name, item }) => ({ name, item }))
  })
]);
</script>
